import React, { useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { PrimaryButton } from '@components/Button/variants';
import { useEventTracking, useOrganizationActiveUsers } from '@hooks';
import { ClickTarget, ContentType, TrackingLocation, TypingInput, click, typing } from '@tracking';
import { ACCESS_ANNUAL } from '@utils/constants';

import SectionLayout from '../../SectionLayout';
import { GroupUser, UserOperationType, UserType } from '../../types';
import PaginatedTable from '../UsersTable/PaginatedTable';
import SearchBar from './SearchBar';

interface ActiveUsersProps {
  availableProSeats: number;
  availableStandardSeats: number;
  onUserClick: (user: GroupUser, operation: UserOperationType) => void;
}
const ActiveUsers = ({
  availableProSeats,
  availableStandardSeats,
  onUserClick,
}: ActiveUsersProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const result = useOrganizationActiveUsers(debouncedSearchTerm);
  const hasTrackedTyping = useRef(false);
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.ActiveUsers,
  });

  const handleUserClick = (user: GroupUser, operation: UserOperationType) => {
    onUserClick(user, operation);
    trackEvent(
      click({
        clickTarget: ClickTarget.UserRow,
        contentId: user.id,
        contentType: ContentType.User,
      })
    );
  };

  const handleAddUser = () => {
    onUserClick(
      { name: '', email: '', accessLevel: ACCESS_ANNUAL, type: UserType.Active },
      UserOperationType.CREATE
    );
    trackEvent(
      click({
        clickTarget: ClickTarget.Button,
        clickText: 'Add user',
      })
    );
  };

  const handleSearchTyping = (term: string) => {
    setSearchTerm(term);
    if (!hasTrackedTyping.current && term.length > 0) {
      hasTrackedTyping.current = true;
      trackEvent(
        typing({
          inputTarget: TypingInput.SearchBar,
        })
      );
    }
  };

  return (
    <Track>
      <SectionLayout
        id="users"
        title="Users"
        sectionId="active-users"
        subtitles={[
          `Standard user seats available: ${availableStandardSeats}`,
          `Pro user seats available: ${availableProSeats}`,
        ]}
        leftElement={
          <PrimaryButton as="button" onClick={handleAddUser} className="!w-[130px]" size="small">
            Add user
          </PrimaryButton>
        }
        rightElement={<SearchBar searchTerm={searchTerm} setSearchTerm={handleSearchTyping} />}
      >
        <PaginatedTable {...result} userType={UserType.Active} onUserClick={handleUserClick} />
      </SectionLayout>
    </Track>
  );
};

export default ActiveUsers;
